import { useLoad } from "@redotech/react-util/load";
import { CustomerInfo } from "@redotech/redo-model/coverage-calculator";
import { AttachmentStrategy } from "@redotech/redo-model/returns/attachment-strategy";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import { useMemo } from "react";
import { ShopifyExtensionClient } from "../../client/shopify-extension-client";
import { StorefrontClient } from "../../client/storefront";
import { getProductInfoFromSource } from "../../eligibility/returns-coverage-eligibility";
import { getFeesCache } from "../../fees-cache";
import { useLocationManager } from "../../location";
import { useBuyerIdentity } from "../../utils/cart";
import { getCoverageInfoFromCoverageCalculators } from "../../utils/coverage-calculations";
import { getCorrectlyPricedVariant } from "../../utils/determine-redo-product";

export function useRescuePanelCoverage({
  cart,
  storefrontClient,
  shopifyExtensionClient,
  autocheck,
}: {
  storefrontClient: StorefrontClient;
  shopifyExtensionClient: ShopifyExtensionClient;
  autocheck: WidgetAutocheck;
  cart: ShopifyCart | undefined;
}) {
  const buyerIdentityLoad = useBuyerIdentity(storefrontClient);

  const feesCacheLoad = useLoad(async () => {
    return await getFeesCache();
  }, []);

  const shouldAddProduct = useMemo(() => {
    return (
      autocheck?.attachmentStrategy ===
        AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT &&
      autocheck.addProductOnOptInClick !== false
    );
  }, [autocheck]);

  const locationManager = useLocationManager();

  const countryLoad = useLoad(
    async (signal) => {
      // Get country from Shopify global or force IP detection if configured
      return (
        (!autocheck.forceIPAddressLocation && globalThis.Shopify?.country) ||
        (await locationManager.getCountry(signal))
      );
    },
    [autocheck.forceIPAddressLocation, locationManager],
  );

  const coverageInfoEligibilityLoad = useLoad(async () => {
    if (!cart) {
      return undefined;
    }

    // Check if using Zapiet pickup method
    const isZapietPickup = cart.attributes?.["Checkout-Method"] === "pickup";

    // Check if it's a business account
    const isBusinessAccount =
      !!buyerIdentityLoad.value?.purchasingCompany?.company;

    // Build customer info for coverage calculation
    const customerInfo: CustomerInfo = {
      customerTags: [], // Customer tags are handled through autocheck currently
      country: countryLoad.value || "",
      isBusinessAccount,
      usingZapietPickup: isZapietPickup,
    };

    const eligibilities = await getCoverageInfoFromCoverageCalculators({
      autocheck,
      customerInfo,
      cart,
      shopifyClient: storefrontClient,
      redoClient: shopifyExtensionClient,
      onTapcart: false,
    });

    return eligibilities;
  }, [
    cart,
    autocheck,
    buyerIdentityLoad,
    storefrontClient,
    shopifyExtensionClient,
  ]);

  const _feeProduct = useMemo(() => {
    if (!cart || !coverageInfoEligibilityLoad.value) {
      return undefined;
    }

    // Determine which product and type based on eligibility
    if (coverageInfoEligibilityLoad.value.both.cartEligible) {
      return feesCacheLoad.value?.bothProduct;
    } else if (
      (autocheck.coverageEnabled ||
        autocheck.finalSaleReturns?.coverageEnabled) &&
      autocheck.packageProtection.coverageEnabled &&
      !autocheck.packageProtection.splitProducts
    ) {
      // Even though returns coverage is enabled AND package protection
      // coverage is enabled, the customer is not eligible for both products,
      // so we respect the splitProducts setting and return undefined.
      return undefined;
    } else if (coverageInfoEligibilityLoad.value.returns.cartEligible) {
      return feesCacheLoad.value?.returnProduct;
    } else if (
      coverageInfoEligibilityLoad.value.packageProtection.cartEligible
    ) {
      return feesCacheLoad.value?.packageProduct;
    }

    return undefined;
  }, [cart, coverageInfoEligibilityLoad, feesCacheLoad, autocheck]);

  const productInfoLoad = useLoad(async () => {
    if (!_feeProduct) {
      return undefined;
    }

    return await getProductInfoFromSource({
      autocheck,
      cartItem: {
        product_id: _feeProduct.productId,
        handle: _feeProduct.handle,
      },
      shopifyClient: storefrontClient,
      redoClient: shopifyExtensionClient,
    });
  }, [_feeProduct, autocheck, storefrontClient, shopifyExtensionClient]);

  const correctVariant = useMemo(() => {
    if (
      !productInfoLoad.value ||
      !productInfoLoad.value.product ||
      !_feeProduct ||
      !coverageInfoEligibilityLoad.value ||
      !shouldAddProduct
    ) {
      return undefined;
    }

    // Get the correctly priced variant if we have a product
    // Create a minimal ShopifyProductJS-compatible object with just the variants we need
    return getCorrectlyPricedVariant({
      product: {
        id: productInfoLoad.value.product.id,
        price_min: 0, // Not used by getCorrectlyPricedVariant
        price_max: 0, // Not used by getCorrectlyPricedVariant
        variants: productInfoLoad.value.product.variants,
      },
      productType: _feeProduct.type,
      coverageInfo: coverageInfoEligibilityLoad.value,
      country: countryLoad.value || "",
    });
  }, [
    _feeProduct,
    productInfoLoad,
    coverageInfoEligibilityLoad,
    countryLoad,
    shouldAddProduct,
  ]);

  return {
    correctVariant,
    coverageInfoEligibilityLoad,
  };
}
